import type { ReactNode } from 'react';
import React, { cloneElement, useRef, useState, useEffect, useCallback } from 'react';
import { get, inRange, isEqual, noop } from 'lodash';
import { ARROW_LEFT, ARROW_RIGHT } from 'Utils/keyCodes';
import { getIndexInRange } from './SliderHandler.helper';
import { SliderHandlerItems } from './SliderHandlerItems';
type Props = {
  centerPadding?: number;
  children: (props: any) => Node;
  className?: string;
  style?: CSSRuleList;
  fadeUnseenItems?: boolean;
  items: ReactNode;
  itemsAreaClassName?: string;
  hasAutoplay?: boolean;
  hasInfiniteScroll?: boolean;
  hasKeyboardSupport?: boolean;
  hasLazyload?: boolean;
  hasSwipeSupport?: boolean;
  initialIndex: number;
  nbItemsToScroll?: number;
  nbItemsToShow: number;
  spacerWidth?: number;
  onChangeIndex?: (arg0: number) => void;
  onNext?: (e?: Event) => void;
  onPrevious?: (e?: Event) => void;
};

type SwipeEndPayload = {
  newIndex: number;
  previousLastIndex: number;
};

function computeInitialState(
  items: ReactNode,
  initialIndex: number,
  nbItemsToShow: number,
  hasLazyload: boolean | undefined,
  nbItemsToScroll: number,
) {
  const itemsCount = items.length;
  const maxIndex = itemsCount < nbItemsToShow ? 0 : Math.ceil(itemsCount - nbItemsToShow);
  const index = getIndexInRange(initialIndex, maxIndex);
  const step = nbItemsToScroll > 0 ? nbItemsToScroll : 1;
  const initialItemsToRender = Array(itemsCount).fill(!hasLazyload, 0, itemsCount);
  const minRange = index - step;
  const maxRange = index + nbItemsToShow + step;
  const itemsToRender = hasLazyload
    ? initialItemsToRender.map(
        (isRendered, itemIndex) => isRendered || inRange(itemIndex, minRange, maxRange),
      )
    : initialItemsToRender;

  return {
    index,
    isSwiping: false,
    isRTL: false,
    itemsCount,
    itemsToRender,
    lastIndex: index,
    maxIndex,
    slideWidth: 0,
    swipeStart: 0,
    swipeStartTime: new Date(),
    swipeX: 0,
  };
}

export function SliderHandler({
  centerPadding = 0,
  children,
  className,
  style,
  fadeUnseenItems,
  items,
  itemsAreaClassName,
  hasAutoplay,
  hasInfiniteScroll,
  hasKeyboardSupport,
  hasLazyload,
  hasSwipeSupport,
  initialIndex = 0,
  nbItemsToScroll = 1,
  nbItemsToShow = 1,
  spacerWidth = 0,
  onChangeIndex,
  onNext: onNextProp,
  onPrevious: onPreviousProp,
}: Props) {
  const initialState = computeInitialState(
    items,
    initialIndex,
    nbItemsToShow,
    hasLazyload,
    nbItemsToScroll,
  );

  const [index, setIndex] = useState(initialState.index);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isRTL, setIsRTL] = useState(false);
  const [itemsCount] = useState(initialState.itemsCount);
  const [itemsToRender, setItemsToRender] = useState(initialState.itemsToRender);
  const [lastIndex, setLastIndex] = useState(initialState.lastIndex);
  const [maxIndex] = useState(initialState.maxIndex);
  const [slideWidth, setSlideWidth] = useState(0);
  const [swipeStart, setSwipeStart] = useState(0);
  const [swipeStartTime, setSwipeStartTime] = useState(new Date());
  const [swipeX, setSwipeX] = useState(0);

  const sliderRef = useRef<HTMLDivElement | null>(null);
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const swipeEndPayloadRef = useRef<SwipeEndPayload | null>(null);

  const getStep = useCallback((): number => {
    return nbItemsToScroll > 0 ? nbItemsToScroll : 1;
  }, [nbItemsToScroll]);

  const getItemsToRender = useCallback(
    (currentItemsToRender: Array<boolean>, idx: number, showCount: number): Array<boolean> => {
      const step = getStep();
      const minRange = idx - step;
      const maxRange = idx + showCount + step;
      const newItemToRender = currentItemsToRender.map(
        (isRendered, itemIndex) => isRendered || inRange(itemIndex, minRange, maxRange),
      );

      if (
        !hasLazyload ||
        currentItemsToRender.every((isRendered) => isRendered) ||
        isEqual(currentItemsToRender, newItemToRender)
      ) {
        return currentItemsToRender;
      }

      return newItemToRender;
    },
    [hasLazyload, getStep],
  );

  const goToIndex = useCallback(
    (newIndex: number, callback: (e?: Event) => void = noop) => {
      if (onChangeIndex) {
        onChangeIndex(newIndex);
      }

      setItemsToRender((prev) => getItemsToRender(prev, newIndex, nbItemsToShow));
      setIndex(newIndex);
      setLastIndex(newIndex);
      callback();
    },
    [onChangeIndex, getItemsToRender, nbItemsToShow],
  );

  const onNext = useCallback(
    (event?: React.SyntheticEvent | KeyboardEvent) => {
      if (event) {
        event.preventDefault();
      }

      setIndex((currentIndex) => {
        let i = currentIndex + getStep();

        if (i > maxIndex) {
          i = hasInfiniteScroll ? 0 : maxIndex;
        }

        goToIndex(i, onNextProp);
        return currentIndex;
      });
    },
    [maxIndex, getStep, hasInfiniteScroll, goToIndex, onNextProp],
  );

  const onPrevious = useCallback(
    (event?: React.SyntheticEvent | KeyboardEvent) => {
      if (event) {
        event.preventDefault();
      }

      setIndex((currentIndex) => {
        const min = 0;
        let i = currentIndex - getStep();

        if (i < min) {
          i = hasInfiniteScroll ? maxIndex + i + 1 : min;
        }

        goToIndex(i, onPreviousProp);
        return currentIndex;
      });
    },
    [maxIndex, getStep, hasInfiniteScroll, goToIndex, onPreviousProp],
  );

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (hasKeyboardSupport) {
        switch (event.which) {
          case ARROW_RIGHT:
            event.stopPropagation();
            isRTL ? onPrevious(event) : onNext(event);
            break;

          case ARROW_LEFT:
            event.stopPropagation();
            isRTL ? onNext(event) : onPrevious(event);
            break;

          default:
        }
      }
    },
    [hasKeyboardSupport, isRTL, onNext, onPrevious],
  );

  const onIndex = useCallback(
    (event?: React.SyntheticEvent | KeyboardEvent, targetIndex: number) => {
      if (event) {
        event.preventDefault();
      }

      if (targetIndex === index) {
        return;
      }

      const isNext = targetIndex > index;
      const callback = isNext ? onNextProp : onPreviousProp;
      goToIndex(targetIndex, callback);
    },
    [index, onNextProp, onPreviousProp, goToIndex],
  );

  useEffect(() => {
    setIndex(initialIndex);
    setLastIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (hasKeyboardSupport && sliderRef.current) {
      const node = sliderRef.current;
      node.addEventListener('keyup', onKeyUp, false);
      return () => {
        node.removeEventListener('keyup', onKeyUp, false);
      };
    }
  }, [hasKeyboardSupport, onKeyUp]);

  useEffect(() => {
    if (hasAutoplay) {
      intervalIdRef.current = setInterval(() => onNext(), 3000);
    }

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [hasAutoplay, onNext]);

  useEffect(() => {
    if (get(document, 'documentElement.dir', 'ltr') === 'rtl') {
      setIsRTL(true);
    }
  }, []);

  useEffect(() => {
    const payload = swipeEndPayloadRef.current;
    if (!payload) {
      return;
    }
    swipeEndPayloadRef.current = null;

    const { newIndex, previousLastIndex } = payload;

    if (onChangeIndex) {
      onChangeIndex(newIndex);
    }

    if (newIndex < previousLastIndex && onPreviousProp) {
      onPreviousProp();
    } else if (newIndex > previousLastIndex && onNextProp) {
      onNextProp();
    }

    setLastIndex(newIndex);
  }, [index, onChangeIndex, onNextProp, onPreviousProp]);

  const getSwipeX = (event: TouchEvent): number => {
    return event.touches[0].pageX;
  };

  const onSwipeStart = useCallback(
    (event: TouchEvent) => {
      if (hasSwipeSupport) {
        event.stopPropagation();
        const swipeStartX = getSwipeX(event);
        const width = sliderRef.current ? sliderRef.current.offsetWidth : 0;
        setSlideWidth(width);
        setIsSwiping(true);
        setSwipeStart(swipeStartX);
        setSwipeX(lastIndex);
        setSwipeStartTime(new Date());
      }
    },
    [hasSwipeSupport, lastIndex],
  );

  const onSwipeMove = useCallback(
    (event: TouchEvent) => {
      if (hasSwipeSupport) {
        event.stopPropagation();
        const x = getSwipeX(event);
        const offset = swipeStart - x;
        const percentageOffset = offset / slideWidth;
        const newSwipeX = isRTL ? lastIndex - percentageOffset : lastIndex + percentageOffset;
        const newIndex = getIndexInRange(Math.round(newSwipeX), maxIndex);
        const newItemsToRender = getItemsToRender(itemsToRender, newIndex, nbItemsToShow);

        setSwipeX(getIndexInRange(newSwipeX, maxIndex));
        if (newItemsToRender !== itemsToRender) {
          setItemsToRender(newItemsToRender);
        }
      }
    },
    [
      hasSwipeSupport,
      swipeStart,
      slideWidth,
      isRTL,
      lastIndex,
      maxIndex,
      getItemsToRender,
      itemsToRender,
      nbItemsToShow,
    ],
  );

  const onSwipeEnd = useCallback(
    (event: TouchEvent) => {
      if (hasSwipeSupport) {
        event.stopPropagation();
        const step = getStep();
        const timeElapsed = new Date().getTime() - swipeStartTime.getTime();
        const offset = lastIndex - swipeX;
        const velocity = Math.round((offset / timeElapsed) * 10000);
        let newIndex = Math.round(swipeX);

        if (Math.abs(velocity) > 5) {
          newIndex = velocity < 0 ? lastIndex + step : lastIndex - step;
        }

        const finalIndex = getIndexInRange(newIndex, maxIndex);
        const newItemsToRender = getItemsToRender(itemsToRender, finalIndex, nbItemsToShow);

        swipeEndPayloadRef.current = {
          newIndex: finalIndex,
          previousLastIndex: lastIndex,
        };

        setIndex(finalIndex);
        setIsSwiping(false);
        setSwipeStart(0);
        setSwipeX(0);
        if (newItemsToRender !== itemsToRender) {
          setItemsToRender(newItemsToRender);
        }
      }
    },
    [
      hasSwipeSupport,
      getStep,
      swipeStartTime,
      lastIndex,
      swipeX,
      maxIndex,
      getItemsToRender,
      itemsToRender,
      nbItemsToShow,
    ],
  );

  const isItemVisible = useCallback(
    (itemIndex: number): boolean => {
      return itemIndex >= index && itemIndex < index + nbItemsToShow;
    },
    [index, nbItemsToShow],
  );

  const shouldRenderItem = useCallback(
    (itemIndex: number): boolean => {
      return !hasLazyload || itemsToRender[itemIndex];
    },
    [hasLazyload, itemsToRender],
  );

  if (!itemsCount) {
    return null;
  }

  const hasControls = itemsCount > nbItemsToShow;
  const x = isSwiping ? swipeX : index;
  const increment = 100 / nbItemsToShow - centerPadding * 100 * 2;
  const posX = x * increment;
  const position = isRTL ? posX : -posX;

  const itemsNode = (
    <SliderHandlerItems
      centerPadding={centerPadding}
      className={className}
      style={style}
      currentIndex={index}
      fadeUnseenItems={fadeUnseenItems}
      hasControls={hasControls}
      isSwiping={isSwiping}
      itemPercentSize={100 / nbItemsToShow}
      itemsAreaClassName={itemsAreaClassName}
      onSwipeEnd={onSwipeEnd}
      onSwipeMove={onSwipeMove}
      onSwipeStart={onSwipeStart}
      position={position}
      ref={sliderRef}
      spacerWidth={spacerWidth}
    >
      {items
        .map((item, idx) =>
          shouldRenderItem(idx)
            ? cloneElement(item, {
                'aria-hidden': !isItemVisible(idx),
                tabIndex: -1,
              })
            : null,
        )
        .filter(Boolean)}
    </SliderHandlerItems>
  );
  const childrenProps = {
    hasControls,
    index,
    isNextControlDisabled: !hasInfiniteScroll && isItemVisible(itemsCount - 1),
    isPreviousControlDisabled: !hasInfiniteScroll && isItemVisible(0),
    itemsNode,
    onNext,
    onPrevious,
    onIndex,
  };
  return children(childrenProps);
}
